import { useState, useEffect } from 'react';
import apiClient from '../../../lib/api';
import { resolveImageUrl } from '../../../lib/utils';

interface ServicesPageContent {
  hero_title: string;
  hero_subtitle: string;
  hero_image_url: string;
}

const ServicesPageManager = () => {
  const [servicesContent, setServicesContent] = useState<ServicesPageContent>({
    hero_title: '',
    hero_subtitle: '',
    hero_image_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settings = await apiClient.getServicesPageSettings();
      setServicesContent(settings);
    } catch (error) {
      console.error('Failed to load services page settings:', error);
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    const div = document.createElement('div');
    div.className = `fixed top-4 right-4 ${type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg z-50`;
    div.innerHTML = `<i class="ri-${type === 'success' ? 'check' : 'error-warning'}-line mr-2"></i>${message}`;
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 3000);
  };

  const handleSaveContent = async () => {
    try {
      setSaving(true);
      await apiClient.updateServicesPageSettings(servicesContent);
      showToast('Services page settings saved successfully!');
      setSelectedFile(null);
    } catch (error) {
      console.error('Failed to save services page settings:', error);
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleHeroImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = (e.target as FileReader | null)?.result as string | undefined;
        if (result) {
          setServicesContent((prev: any) => ({
            ...prev,
            hero_image_url: result
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900">Services Page Management</h3>
        <button
          onClick={handleSaveContent}
          disabled={saving}
          className="bg-primary-400 hover:bg-primary-500 text-black px-6 py-2 rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Hero Section */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h4 className="text-lg font-bold text-gray-900 mb-4">Hero Section</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Title</label>
            <input
              type="text"
              value={servicesContent.hero_title}
              onChange={(e) => setServicesContent((prev: any) => ({ ...prev, hero_title: e.currentTarget.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Subtitle</label>
            <textarea
              value={servicesContent.hero_subtitle}
              onChange={(e) => setServicesContent(prev => ({ ...prev, hero_subtitle: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Upload Hero Image</label>
            <div className="space-y-2">
              <input
                type="file"
                accept="image/*"
                onChange={handleHeroImageUpload}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
              />
              <p className="text-sm text-gray-600">or enter image URL below</p>
            </div>
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Hero Image URL</label>
            <input
              type="url"
              value={servicesContent.hero_image_url}
              onChange={(e) => setServicesContent(prev => ({ ...prev, hero_image_url: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
            />
            {servicesContent.hero_image_url && (
              <img
                src={resolveImageUrl(servicesContent.hero_image_url)}
                alt="Hero Preview"
                className="mt-2 w-full h-40 object-cover rounded-lg"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPageManager;
